import React, { useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import api from '../utils/api';
import './BookSessionModal.css';

// Quick enquiry modal: name + mobile + email only. POSTs to the existing
// /api/enquiries.php — when consultantId is set the enquiry also notifies
// that practitioner directly (enquiries.php routing), otherwise it goes to
// the global admin recipients under `source`.
// Two usages: pass expertName for the team-card "Book Session" flow (all
// other props default from it), or override title/subtitle/topic/message/
// source/submitLabel/doneMessage for a generic enquiry (e.g. the
// ConditionsGrid "Need help with <condition>" cards).
function BookSessionModal({
  expertName,
  consultantId,
  onClose,
  title = 'Book a session',
  subtitle = `with ${expertName}`,
  topic = `Session with ${expertName}`,
  message = `Session booking request from the homepage team section (expert: ${expertName}).`,
  source = 'home-team',
  submitLabel = 'Request Session',
  doneMessage = `Thanks — we’ll reach out shortly to schedule your session with ${expertName}.`,
}) {
  const [form, setForm] = useState({ name: '', phone: '', email: '' });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState('');

  // Lock body scroll while open; close on Escape.
  useEffect(() => {
    const original = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = original;
      window.removeEventListener('keydown', onKey);
    };
  }, [onClose]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const validate = () => {
    if (!form.name.trim()) return 'Please enter your name.';
    if (!/^\+?[0-9\s-]{7,15}$/.test(form.phone.trim())) return 'Please enter a valid mobile number.';
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) return 'Please enter a valid email address.';
    return '';
  };

  const submit = async (e) => {
    e.preventDefault();
    const v = validate();
    if (v) { setError(v); return; }
    setError('');
    setBusy(true);
    try {
      await api.submitEnquiry({
        name: form.name.trim(),
        phone: form.phone.trim(),
        email: form.email.trim(),
        topic,
        message,
        consultant_id: consultantId || undefined,
        source,
      });
      setDone(true);
    } catch (err) {
      setError(err && err.message ? err.message : 'Something went wrong — please try again.');
    } finally {
      setBusy(false);
    }
  };

  return ReactDOM.createPortal(
    <div
      className="book-modal"
      role="dialog"
      aria-modal="true"
      aria-label={`${title} ${subtitle}`.trim()}
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="book-modal__card">
        <button type="button" className="book-modal__close" aria-label="Close" onClick={onClose}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
            <line x1="6" y1="6" x2="18" y2="18" />
            <line x1="18" y1="6" x2="6" y2="18" />
          </svg>
        </button>

        {done ? (
          <div className="book-modal__done">
            <div className="book-modal__done-icon" aria-hidden="true">
              <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="20 6 9 17 4 12" />
              </svg>
            </div>
            <h3>Request received</h3>
            <p>{doneMessage}</p>
            <button type="button" className="btn-primary" onClick={onClose}>Done</button>
          </div>
        ) : (
          <>
            <h3 className="book-modal__title">{title}</h3>
            {subtitle && <p className="book-modal__sub">{subtitle}</p>}
            <form onSubmit={submit} noValidate>
              <label className="book-modal__field">
                <span>Your name</span>
                <input type="text" value={form.name} onChange={set('name')} autoComplete="name" required />
              </label>
              <label className="book-modal__field">
                <span>Mobile</span>
                <input type="tel" value={form.phone} onChange={set('phone')} autoComplete="tel" required />
              </label>
              <label className="book-modal__field">
                <span>Email</span>
                <input type="email" value={form.email} onChange={set('email')} autoComplete="email" required />
              </label>
              {error && <p className="book-modal__error" role="alert">{error}</p>}
              <button type="submit" className="btn-primary book-modal__submit" disabled={busy}>
                {busy ? 'Sending…' : submitLabel}
              </button>
            </form>
          </>
        )}
      </div>
    </div>,
    document.body
  );
}

export default BookSessionModal;
